import { useAnimation, useSelectedObject } from '@/context/AnimationContext';
import { useI18n } from '@/context/I18nContext';
import { getObjectPropsAtFrame } from '@/utils/interpolation';
import { easings } from '@/utils/interpolation';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CharacterEditor } from '@/components/editor/CharacterEditor';
import { Textarea } from '@/components/ui/textarea';
import { KeyRound, Trash2, Palette, Type } from 'lucide-react';

function PropInput({ label, value, onChange, type = 'number', step = 1, min, max, testId }) {
  return (
    <div className="flex items-center gap-2">
      <Label className="text-[10px] text-muted-foreground/70 font-mono w-8 shrink-0 text-right">{label}</Label>
      <Input
        type={type}
        value={type === 'number' ? Math.round(value * 100) / 100 : value}
        onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
        step={step}
        min={min}
        max={max}
        className="h-6 text-[11px] font-mono bg-white/[0.03] border-white/[0.06] hover:border-white/[0.12] focus:border-rose-400/50 rounded"
        data-testid={testId}
      />
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <p className="text-[9px] font-medium text-muted-foreground/50 uppercase tracking-[0.15em] mb-2">{children}</p>
  );
}

export function PropertyPanel() {
  const { state, dispatch } = useAnimation();
  const { t } = useI18n();
  const selectedObj = useSelectedObject();

  if (!selectedObj) {
    return (
      <div className="h-full flex items-center justify-center p-4" data-testid="property-panel-empty">
        <div className="text-center">
          <div className="w-10 h-10 rounded-full bg-white/[0.03] flex items-center justify-center mx-auto mb-3">
            <MouseIcon className="w-4 h-4 text-muted-foreground/40" />
          </div>
          <p className="text-[11px] text-muted-foreground/50 font-mono">
            {t('selectObject')}
          </p>
        </div>
      </div>
    );
  }

  const animProps = getObjectPropsAtFrame(selectedObj, state.currentFrame);
  const currentX = animProps.x ?? selectedObj.x;
  const currentY = animProps.y ?? selectedObj.y;
  const currentRotation = animProps.rotation ?? selectedObj.rotation ?? 0;
  const currentScaleX = animProps.scaleX ?? selectedObj.scaleX ?? 1;
  const currentScaleY = animProps.scaleY ?? selectedObj.scaleY ?? 1;
  const currentOpacity = animProps.opacity ?? selectedObj.opacity ?? 1;

  const updateProp = (props) => {
    dispatch({
      type: 'UPDATE_OBJECT',
      payload: { id: selectedObj.id, props, autoKey: true }
    });
  };

  const addKeyframe = () => {
    dispatch({
      type: 'ADD_KEYFRAME',
      payload: { objectId: selectedObj.id, frame: state.currentFrame }
    });
  };

  const deleteObject = () => {
    dispatch({ type: 'REMOVE_OBJECT', payload: selectedObj.id });
  };

  const hasKeyframeAtFrame = selectedObj.keyframes?.some(k => k.frame === state.currentFrame);

  return (
    <ScrollArea className="h-full" data-testid="property-panel">
      <div className="p-3 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-medium font-display">{selectedObj.name}</p>
            <p className="text-[9px] text-muted-foreground/50 font-mono">{selectedObj.type}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground/40 hover:text-red-400 rounded-full"
            onClick={deleteObject}
            data-testid="delete-object-button"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>

        <div className="h-px bg-white/[0.04]" />

        {/* Transform */}
        <div>
          <SectionTitle>{t('position')}</SectionTitle>
          <div className="space-y-1.5">
            <PropInput label="X" value={currentX} onChange={(v) => updateProp({ x: v })} testId="property-input-x" />
            <PropInput label="Y" value={currentY} onChange={(v) => updateProp({ y: v })} testId="property-input-y" />
          </div>
        </div>

        <div>
          <SectionTitle>{t('size')}</SectionTitle>
          <div className="space-y-1.5">
            <PropInput label="W" value={selectedObj.width} onChange={(v) => updateProp({ width: v })} testId="property-input-w" />
            <PropInput label="H" value={selectedObj.height} onChange={(v) => updateProp({ height: v })} testId="property-input-h" />
            <PropInput label="Rot" value={currentRotation} onChange={(v) => updateProp({ rotation: v })} step={1} testId="property-input-rotation" />
          </div>
        </div>

        <div className="h-px bg-white/[0.04]" />

        {/* Scale */}
        <div>
          <SectionTitle>{t('scale')}</SectionTitle>
          <div className="space-y-1.5">
            <PropInput label="SX" value={currentScaleX} onChange={(v) => updateProp({ scaleX: v })} step={0.01} min={0.01} testId="property-input-scale-x" />
            <PropInput label="SY" value={currentScaleY} onChange={(v) => updateProp({ scaleY: v })} step={0.01} min={0.01} testId="property-input-scale-y" />
          </div>
        </div>

        <div className="h-px bg-white/[0.04]" />

        {/* Appearance */}
        <div>
          <SectionTitle>{t('appearance')}</SectionTitle>
          <div className="space-y-2.5">
            <div className="flex items-center gap-2">
              <Label className="text-[10px] text-muted-foreground/70 font-mono w-8 shrink-0 text-right">Op</Label>
              <Slider
                value={[currentOpacity * 100]}
                onValueChange={([v]) => updateProp({ opacity: v / 100 })}
                min={0}
                max={100}
                step={1}
                className="flex-1"
                data-testid="property-input-opacity"
              />
              <span className="text-[10px] font-mono text-muted-foreground/50 w-8 text-right tabular-nums">
                {Math.round(currentOpacity * 100)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-[10px] text-muted-foreground/70 font-mono w-8 shrink-0 text-right">Fill</Label>
              <div className="flex items-center gap-1.5 flex-1">
                <input
                  type="color"
                  value={selectedObj.fill || '#f8a4c8'}
                  onChange={(e) => updateProp({ fill: e.target.value })}
                  className="w-6 h-6 rounded border border-white/[0.08] cursor-pointer bg-transparent"
                  data-testid="property-input-fill"
                />
                <span className="text-[10px] font-mono text-muted-foreground/50">{selectedObj.fill}</span>
              </div>
            </div>

            {selectedObj.stroke !== undefined && (
              <div className="flex items-center gap-2">
                <Label className="text-[10px] text-muted-foreground/70 font-mono w-8 shrink-0 text-right">Strk</Label>
                <div className="flex items-center gap-1.5 flex-1">
                  <input
                    type="color"
                    value={selectedObj.stroke || '#000000'}
                    onChange={(e) => updateProp({ stroke: e.target.value })}
                    className="w-6 h-6 rounded border border-white/[0.08] cursor-pointer bg-transparent"
                    data-testid="property-input-stroke"
                  />
                  <Input
                    type="number"
                    value={selectedObj.strokeWidth || 0}
                    onChange={(e) => updateProp({ strokeWidth: parseFloat(e.target.value) || 0 })}
                    className="h-6 text-[11px] font-mono w-12 bg-white/[0.03] border-white/[0.06] rounded"
                    min={0}
                    data-testid="property-input-stroke-width"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Text Editing - only for text objects */}
        {selectedObj.type === 'text' && (
          <>
            <div className="h-px bg-white/[0.04]" />
            <div>
              <SectionTitle>
                <span className="flex items-center gap-1"><Type className="w-3 h-3" /> {t('text') || 'Text'}</span>
              </SectionTitle>
              <div className="space-y-2">
                <Textarea
                  value={selectedObj.text || ''}
                  onChange={(e) => updateProp({ text: e.target.value })}
                  placeholder={t('enterText') || 'Zadejte text...'}
                  className="text-[11px] font-mono bg-white/[0.03] border-white/[0.06] hover:border-white/[0.12] focus:border-rose-400/50 rounded resize-none min-h-[60px]"
                  data-testid="property-input-text"
                />
                <PropInput
                  label="Size"
                  value={selectedObj.fontSize || 24}
                  onChange={(v) => updateProp({ fontSize: v })}
                  min={8}
                  max={200}
                  step={1}
                  testId="property-input-font-size"
                />
              </div>
            </div>
          </>
        )}

        {/* Character Editor - only for character types */}
        {['character', 'agata', 'blondeBallerina', 'sun', 'singer'].includes(selectedObj.type) && (
          <>
            <div className="h-px bg-white/[0.04]" />
            <div>
              <SectionTitle>{t('characterEditor')}</SectionTitle>
              <CharacterEditor
                trigger={
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-[10px] w-full rounded-lg bg-white/[0.03] border-white/[0.06] text-muted-foreground hover:bg-violet-500/10 hover:text-violet-300 hover:border-violet-500/20"
                    data-testid="open-character-editor"
                  >
                    <Palette className="w-3 h-3 mr-1.5" />
                    {t('editColors')}
                  </Button>
                }
              />
            </div>
          </>
        )}

        <div className="h-px bg-white/[0.04]" />

        {/* Keyframe */}
        <div>
          <SectionTitle>{t('keyframe')}</SectionTitle>
          <Button
            variant={hasKeyframeAtFrame ? "default" : "outline"}
            size="sm"
            className={`h-7 text-[10px] w-full rounded-lg ${
              hasKeyframeAtFrame
                ? 'bg-amber-500/15 text-amber-300 border border-amber-500/20 hover:bg-amber-500/25'
                : 'bg-white/[0.03] border-white/[0.06] text-muted-foreground hover:bg-white/[0.06]'
            }`}
            onClick={addKeyframe}
            data-testid="add-keyframe-button"
          >
            <KeyRound className="w-3 h-3 mr-1.5" />
            {hasKeyframeAtFrame ? `${t('keyframeAt')} ${state.currentFrame}` : t('addKeyframe')}
          </Button>
          <p className="text-[9px] text-muted-foreground/40 font-mono mt-2">
            {selectedObj.keyframes?.length || 0} {t('keyframes')}
          </p>
        </div>

        <div className="h-px bg-white/[0.04]" />

        {/* Easing */}
        <div>
          <SectionTitle>{t('easing')}</SectionTitle>
          <Select
            value={selectedObj.easing || 'easeInOut'}
            onValueChange={(v) => updateProp({ easing: v })}
          >
            <SelectTrigger className="h-7 text-[10px] font-mono bg-white/[0.03] border-white/[0.06]" data-testid="easing-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(220,14%,11%)] border-white/[0.08]">
              {Object.keys(easings).map(name => (
                <SelectItem key={name} value={name} className="text-[10px] font-mono">
                  {t(`easing_${name}`) || name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[9px] text-muted-foreground/40 font-mono mt-1.5">
            {t('easingHint')}
          </p>
        </div>
      </div>
    </ScrollArea>
  );
}

function MouseIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 3 7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
      <path d="m13 13 6 6" />
    </svg>
  );
}
